import React, { useState, useEffect, useRef } from 'react';
import { WebsiteDesignProps } from './types';
import { 
  Palette, Music, Camera, Star, ArrowRight, ArrowUpRight, 
  Menu, X, Zap, Heart, Users, Trophy, School, Clock, 
  MapPin, Phone, Mail, Globe, GraduationCap, ChevronLeft, 
  ChevronRight, ChevronDown, Download, Send, Plus, Layout, Sparkles, 
  FileText, Instagram, Facebook, Linkedin, Youtube, Quote, BookOpen
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from "@/components/ui/button";
import { VisualImageEditable } from "@/components/VisualImageEditable";
import { VisualTextEditable } from "@/components/VisualTextEditable";

const CreativeArts: React.FC<WebsiteDesignProps> = ({ previewData, isEditMode, onUpdateField }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLoginDropdown, setShowLoginDropdown] = useState(false);
  const [activeGalleryTab, setActiveGalleryTab] = useState("All");
  const containerRef = useRef<HTMLDivElement>(null);

  // Icon resolver helper
  const getIcon = (name: string | any) => {
      if (!name) return Star;
      if (typeof name !== 'string') return name;
      // @ts-ignore
      const Icon = LucideIcons[name];
      return Icon || Star;
  };

  const schoolName = previewData?.name || "Muse School of Arts";

  // Shared Data Model (Strict Matching Design 1)
  const defaultData = {
    hero: {
      images: ["https://images.unsplash.com/photo-1547891261-81134017684d?q=80&w=2070&auto=format&fit=crop"],
      tagline: "Est. 1992 // Breeding Visionaries",
      titlePrefix: "Where Ideas",
      subtitle: "An experimental ground for the next generation of artists, designers, and visionaries. We focus on raw creativity and technical mastery."
    },
    features: [
      { icon: "Palette", title: "Fine Arts", desc: "A classical foundation for contemporary expression." },
      { icon: "Music", title: "Sound Design", desc: "Crafting atmospheric worlds through audio engineering." },
      { icon: "Camera", title: "Digital Media", desc: "Defining the future of visual storytelling." }
    ],
    programs: [
      { title: "Undergraduate", age: "Bachelor of Arts", desc: "Our flagship programs for foundation and specialization.", img: "https://images.unsplash.com/photo-1513364776144-60967b0f800f?q=80&w=2071&auto=format&fit=crop" },
      { title: "Postgraduate", age: "Master of Fine Arts", desc: "Advanced research and experimental practice.", img: "https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?q=80&w=1760&auto=format&fit=crop" }
    ],
    stats: [
        { value: "40+", label: "Gallery Awards" },
        { value: "85%", label: "Industry Placement" },
        { value: "1:8", label: "Artist-Student Ratio" }
    ],
    news: [
        { title: "Annual Exhibition 2026", date: "FEB 12", desc: "Showcasing the raw potential of our graduating visionaries.", img: "https://images.unsplash.com/photo-1456086272160-b28b0645b729?q=80&w=1932&auto=format&fit=crop" }
    ],
    nav: [
        { label: 'Manifesto', href: '#about' },
        { label: 'Programs', href: '#programs' },
        { label: 'Exhibition', href: '#gallery' },
        { label: 'Studio', href: '#facilities' }
    ],
    team: [
        { name: "Adrian Roth", role: "Creative Director", img: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=2070&auto=format&fit=crop" },
        { name: "Elena Volkov", role: "Digital Lead", img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=1976&auto=format&fit=crop" }
    ],
    gallery: [
        { category: "Digital", img: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2070&auto=format&fit=crop", title: "Neon Dreams" },
        { category: "Arts", img: "https://images.unsplash.com/photo-1513364776144-60967b0f800f?q=80&w=2071&auto=format&fit=crop", title: "Canvas Flow" }
    ],
    facilities: [
        { title: "VR Studio", desc: "Immersive development environment.", img: "https://images.unsplash.com/photo-1478416272538-5f7e51dc5400?q=80&w=2065&auto=format&fit=crop", icon: "Camera" },
        { title: "The Darkroom", desc: "Classical film development labs.", img: "https://images.unsplash.com/photo-1614850523296-d8c1af93d400?q=80&w=2070&auto=format&fit=crop", icon: "Zap" }
    ]
  };

  const mockData = {
      ...previewData,
      hero: {
          titlePrefix: previewData?.hero?.titlePrefix || defaultData.hero.titlePrefix,
          tagline: previewData?.hero?.tagline || defaultData.hero.tagline,
          subtitle: previewData?.hero?.subtitle || defaultData.hero.subtitle,
          images: previewData?.hero?.images || defaultData.hero.images,
          backgroundImage: previewData?.hero?.backgroundImage || defaultData.hero.images[0]
      },
      about: {
          title: previewData?.about?.title || "Defying the Standard",
          description: previewData?.about?.description || "At " + schoolName + ", we don't just teach art. We define culture. Our curriculum is built on the intersection of raw human emotion and digital precision.",
          sectionImage: previewData?.about?.sectionImage || "https://images.unsplash.com/photo-1513364776144-60967b0f800f?q=80&w=2071&auto=format&fit=crop"
      },
      features: (previewData?.features || defaultData.features).map((f: any) => ({ 
        ...f, 
        icon: getIcon(f.icon),
        desc: f.description || f.desc || ""
      })),
      programs: (previewData?.programs || defaultData.programs).map((p: any) => ({
        title: p.name || p.title || "",
        age: p.age || "",
        desc: p.description || p.desc || "",
        img: p.image || p.img || ""
      })),
      stats: (previewData?.stats || defaultData.stats).map((s: any) => ({
          value: s.value || "0",
          label: s.label || ""
      })),
      news: (previewData?.news || defaultData.news).map((n: any) => ({
        title: n.title || "",
        date: n.date || "",
        desc: n.description || n.desc || "",
        img: n.image || n.img || ""
      })),
      team: (previewData?.team || defaultData.team).map((t: any) => ({
        name: t.name || "",
        role: t.role || "",
        img: t.img || t.image || "",
        linkedin: t.linkedin || "",
        email: t.email || ""
      })),
      gallery: (previewData?.gallery || defaultData.gallery).map((g: any) => ({
        category: g.category || "General",
        title: g.caption || g.title || "",
        img: g.image || g.img || ""
      })),
      facilities: (previewData?.facilities || defaultData.facilities).map((f: any) => ({ 
        ...f, 
        icon: getIcon(f.icon),
        desc: f.description || f.desc || "",
        img: f.image || f.img || ""
      })),
      downloads: (previewData?.downloads || [
          { title: "Manifesto 2026", size: "1.2 MB", type: "PDF" },
          { title: "Studio Policies", size: "0.5 MB", type: "PDF" }
      ]).map((d: any) => ({
          title: d.title || "",
          size: d.size || "",
          type: d.type || "PDF"
      })),
      contact: {
        address: previewData?.contact?.address || "Building Zero, Neon District, Nairobi",
        phone: previewData?.contact?.phone || "+254 711 222 333",
        email: previewData?.contact?.email || "muse@studio.arts",
        officeHours: previewData?.contact?.officeHours || "Mon - Sat: 10:00 AM - 10:00 PM",
        facebook: previewData?.contact?.facebook || "",
        instagram: previewData?.contact?.instagram || "",
        youtube: previewData?.contact?.youtube || "",
        linkedin: previewData?.contact?.linkedin || ""
      },
      sectionVisibility: {
          hero: previewData?.sectionVisibility?.hero !== false,
          about: previewData?.sectionVisibility?.about !== false,
          stats: previewData?.sectionVisibility?.stats !== false,
          features: previewData?.sectionVisibility?.features !== false,
          programs: previewData?.sectionVisibility?.programs !== false,
          facilities: previewData?.sectionVisibility?.facilities !== false,
          team: previewData?.sectionVisibility?.team !== false,
          gallery: previewData?.sectionVisibility?.gallery !== false,
          news: previewData?.sectionVisibility?.news !== false,
          downloads: previewData?.sectionVisibility?.downloads !== false,
          cta: previewData?.sectionVisibility?.cta !== false,
          contact: previewData?.sectionVisibility?.contact !== false
      },
      nav: (previewData?.nav || defaultData.nav).map((n: any) => ({
          label: n.label || "",
          href: n.href || "#",
          visible: n.visible !== false
      })),
      labels: {
          downloadsSection: previewData?.labels?.downloadsSection || "Resource Vault",
          downloadsTitle: previewData?.labels?.downloadsTitle || "Digital Assets",
          contactFormTitle: previewData?.labels?.contactFormTitle || "Contact Form",
          contactFormSubtitle: previewData?.labels?.contactFormSubtitle || "Fill out the form below."
      }
  };

  useEffect(() => {
    const doc = containerRef.current?.ownerDocument || document;
    const root = doc.documentElement;
    if (previewData?.theme) {
        const { colors, fonts } = previewData.theme;
        root.style.setProperty('--color-primary', colors.primary || '#ff0066');
        root.style.setProperty('--color-secondary', colors.secondary || '#8a2be2');
        root.style.setProperty('--color-accent', colors.accent || '#00ffcc');
        root.style.setProperty('--color-background', colors.background || '#0a0a0a');
        root.style.setProperty('--color-text', colors.text || '#ffffff');
        root.style.setProperty('--font-heading', `'${fonts.heading || 'Staatliches'}', sans-serif`);
        root.style.setProperty('--font-body', `'${fonts.body || 'Inter'}', sans-serif`);
        const radiusMap = { none: '0px', small: '0px', medium: '0px', large: '0px', full: '9999px' };
        root.style.setProperty('--radius', radiusMap[previewData.theme.borderRadius || 'none']);
    }
    const fontLink = doc.createElement('link');
    fontLink.href = "https://fonts.googleapis.com/css2?family=Staatliches&family=Inter:wght@300;400;700;900&display=swap";
    fontLink.rel = 'stylesheet';
    doc.head.appendChild(fontLink);
  }, [previewData?.theme]);

  const handleUpdate = (path: string, val: any) => onUpdateField && onUpdateField(path, val);
  const addArrayItem = (path: string, newItem: any) => {
    if (onUpdateField) {
        const arr = [...((mockData as any)[path] || [])];
        onUpdateField(path, [...arr, newItem]);
    }
  };
  const removeArrayItem = (path: string, index: number) => {
    if (onUpdateField && window.confirm('Discard this item?')) {
        const arr = [...((mockData as any)[path] || [])];
        onUpdateField(path, arr.filter((_, i) => i !== index));
    }
  };

  return (
    <div ref={containerRef} className="creative-arts bg-[var(--color-background)] text-[var(--color-text)] min-h-screen font-[var(--font-body)] selection:bg-[var(--color-primary)] selection:text-white overflow-x-hidden">
      
      {/* Dynamic Nav - Glass & Neon */}
      <nav className="fixed top-0 w-full z-50 p-6 flex justify-between items-center transition-all duration-300">
          <div className="flex items-center gap-6">
               <VisualImageEditable 
                  src={previewData?.logoUrl || ""} 
                  alt="Logo" 
                  containerClassName="w-12 h-12 bg-white flex items-center justify-center p-1"
                  className="w-full h-full object-contain"
                  onImageChange={(u) => handleUpdate('logoUrl', u)}
                  isEditMode={!!isEditMode}
                  fallback={<Palette size={24} className="text-black" />}
              />
              <div className="bg-white text-black px-4 py-2 font-black uppercase text-xl leading-none">
                  <VisualTextEditable value={schoolName} onSave={(v) => handleUpdate('name', v)} isEditMode={!!isEditMode} />
              </div>
          </div>
          
          <div className="hidden lg:flex items-center gap-12">
              <div className="flex gap-10 text-[11px] font-black uppercase tracking-[0.2em] mix-blend-difference">
                  {mockData.nav.filter(n => n.visible).map((n, i) => (
                      <a key={i} href={n.href} className="hover:text-[var(--color-primary)] transition-all">
                          {n.label}
                      </a>
                  ))}
              </div>
              <div className="relative">
                  <button onClick={() => setShowLoginDropdown(!showLoginDropdown)} className="bg-[var(--color-primary)] px-8 py-3 text-[11px] font-black uppercase tracking-widest text-white shadow-[8px_8px_0px_#ffffff20] hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-all flex items-center gap-2">
                       Portals <ChevronDown size={14} className={showLoginDropdown ? 'rotate-180' : ''} />
                  </button>
                  {showLoginDropdown && (
                      <div className="absolute top-full right-0 mt-4 w-56 bg-white p-2 z-[60] shadow-2xl border-4 border-black">
                          <a href="https://sonoma.school/guardian-login" className="flex items-center gap-3 p-4 text-[10px] font-black uppercase tracking-widest text-black hover:bg-[var(--color-primary)] hover:text-white transition-all">
                              <Heart size={14} /> Parent Entrance
                          </a>
                          <a href="https://sonoma.school/student-login" className="flex items-center gap-3 p-4 text-[10px] font-black uppercase tracking-widest text-black hover:bg-[var(--color-primary)] hover:text-white transition-all">
                              <Star size={14} /> Student Gate
                          </a>
                      </div>
                  )}
              </div>
          </div>

          <button className="lg:hidden bg-white text-black p-3" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
      </nav>

      {/* Hero Section - Maximum Typography */}
      {mockData.sectionVisibility.hero && (
      <section className="relative min-h-screen flex flex-col justify-center px-6 overflow-hidden pt-20">
          <div className="absolute top-[-20%] right-[-10%] w-[80vw] lg:w-[1000px] aspect-square bg-[var(--color-primary)] rounded-full blur-[200px] opacity-20 animate-pulse" />
          <div className="absolute bottom-[-10%] left-[-10%] w-[600px] aspect-square bg-[var(--color-secondary)] rounded-full blur-[150px] opacity-20" />
          
          <div className="container mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-8">
                  <div className="inline-block border-2 border-[var(--color-accent)] px-4 py-1 mb-8">
                      <span className="text-[11px] font-black text-[var(--color-accent)] uppercase tracking-[0.4em]">
                          <VisualTextEditable value={mockData.hero.tagline} onSave={(v) => handleUpdate('hero.tagline', v)} isEditMode={!!isEditMode} />
                      </span>
                  </div>
                  <h1 className="text-[16vw] lg:text-[12vw] leading-[0.85] font-black uppercase tracking-tighter mix-blend-screen opacity-100">
                      <VisualTextEditable value={mockData.hero.titlePrefix} onSave={(v) => handleUpdate('hero.titlePrefix', v)} isEditMode={!!isEditMode} /><br/>
                      <span className="text-[var(--color-primary)] italic -translate-x-2 block">
                          <VisualTextEditable value={schoolName} onSave={(v) => handleUpdate('name', v)} isEditMode={!!isEditMode} />
                      </span>
                  </h1>
                  <p className="mt-12 max-w-xl text-xl md:text-2xl text-gray-400 font-light leading-relaxed border-l-4 border-[var(--color-secondary)] pl-8">
                      <VisualTextEditable value={mockData.hero.subtitle} onSave={(v) => handleUpdate('hero.subtitle', v)} isEditMode={!!isEditMode} multiline />
                  </p>
              </div>
              
              <div className="lg:col-span-4 relative">
                  <div className="relative aspect-[3/4] border-8 border-white p-4 group">
                       <VisualImageEditable 
                          src={mockData.hero.backgroundImage} 
                          className="w-full h-full object-cover grayscale brightness-50 group-hover:grayscale-0 group-hover:brightness-100 transition-all duration-1000" 
                          alt="Hero"
                          onImageChange={(u) => handleUpdate('hero.backgroundImage', u)}
                          isEditMode={!!isEditMode}
                      />
                      <div className="absolute -bottom-8 -left-8 right-8 bg-[var(--color-primary)] p-8 shadow-2xl">
                          <div className="text-[10px] font-black uppercase tracking-widest text-white/50 mb-2">NOW OPEN</div>
                          <h4 className="text-2xl font-black uppercase italic leading-none">Admission Cycle 2026/27</h4>
                      </div>
                  </div>
              </div>
          </div>
      </section>
      )}

      {/* Stats - Neon Strip */}
      {mockData.sectionVisibility.stats && (
      <section className="bg-white py-12 px-6">
          <div className="container mx-auto flex flex-wrap justify-center md:justify-between items-center gap-12 overflow-hidden">
              {mockData.stats.map((stat, i) => (
                  <div key={i} className="flex flex-col items-center md:items-start group">
                      <div className="text-7xl md:text-9xl font-black text-black leading-none mb-2 tracking-tighter group-hover:text-[var(--color-primary)] transition-colors">
                          <VisualTextEditable value={stat.value} onSave={(v) => handleUpdate(`stats[${i}].value`, v)} isEditMode={!!isEditMode} />
                      </div>
                      <div className="text-[10px] font-black uppercase tracking-[0.5em] text-gray-400">
                          <VisualTextEditable value={stat.label} onSave={(v) => handleUpdate(`stats[${i}].label`, v)} isEditMode={!!isEditMode} />
                      </div>
                  </div>
              ))}
          </div>
      </section>
      )}

      {/* About - Manifesto Layout */}
      {mockData.sectionVisibility.about && (
      <section id="about" className="py-40 px-6 relative overflow-hidden">
          <div className="container mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
                  <div className="order-2 lg:order-1">
                      <div className="relative aspect-square">
                          <div className="absolute inset-0 border-4 border-[var(--color-secondary)] translate-x-10 translate-y-10" />
                          <VisualImageEditable 
                              src={mockData.about.sectionImage} 
                              className="w-full h-full object-cover relative z-10 grayscale hover:grayscale-0 transition-all duration-1000"
                              onImageChange={(u) => handleUpdate('about.sectionImage', u)}
                              isEditMode={!!isEditMode}
                          />
                          <div className="absolute -top-10 -right-10 w-40 h-40 bg-[var(--color-primary)] flex items-center justify-center rotate-12 z-20 shadow-2xl">
                               <Sparkles size={64} className="text-white animate-spin-slow" />
                          </div>
                      </div>
                  </div>
                  <div className="order-1 lg:order-2">
                       <h2 className="text-[8vw] lg:text-[6vw] font-black uppercase tracking-tighter leading-none mb-12">
                          <VisualTextEditable value={mockData.about.title} onSave={(v) => handleUpdate('about.title', v)} isEditMode={!!isEditMode} />
                      </h2>
                      <div className="text-2xl md:text-3xl font-light text-gray-400 mb-16 italic border-r-8 border-[var(--color-primary)] pr-12 text-right">
                          <VisualTextEditable value={mockData.about.description} onSave={(v) => handleUpdate('about.description', v)} isEditMode={!!isEditMode} multiline />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-12">
                          <div className="p-10 border-2 border-white/5 hover:border-[var(--color-accent)] transition-colors group">
                               <h4 className="text-xl font-black uppercase mb-4 text-[var(--color-accent)]">Manifesto 01</h4>
                               <p className="text-sm text-gray-400 leading-relaxed font-light">We prioritize failure as a pathway to innovation. Perfection is the death of original thought.</p>
                          </div>
                          <div className="p-10 border-2 border-white/5 hover:border-[var(--color-secondary)] transition-colors group">
                               <h4 className="text-xl font-black uppercase mb-4 text-[var(--color-secondary)]">Manifesto 02</h4>
                               <p className="text-sm text-gray-400 leading-relaxed font-light">Collaborate or dissolve. The future is interdisciplinary, defined by collective intelligence.</p>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
      </section>
      )}

      {/* Features - High Contrast Cards */}
      {mockData.sectionVisibility.features && (
      <section className="py-40 bg-white text-black px-6">
          <div className="container mx-auto">
              <div className="flex flex-col md:flex-row justify-between items-end gap-12 mb-24 border-b-8 border-black pb-12">
                  <h2 className="text-7xl md:text-[10rem] font-black uppercase tracking-tighter leading-none ">Pillars.</h2>
                  <p className="text-right text-[11px] font-black uppercase tracking-[0.4em] text-gray-400 max-w-xs mb-4">The core disciplines that form our experimental framework.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-r-8 border-b-8 border-black">
                  {mockData.features.map((item, i) => (
                      <div key={i} className="group p-16 border-l-8 border-t-8 border-black hover:bg-black hover:text-white transition-all duration-500 relative overflow-hidden">
                          <div className="absolute top-0 right-0 p-8 text-black group-hover:text-white opacity-10 font-black text-9xl leading-none px-4 -translate-y-4">0{i+1}</div>
                          <div className="w-20 h-20 bg-black text-white group-hover:bg-white group-hover:text-black flex items-center justify-center mb-10 transition-colors">
                              {React.createElement(item.icon, { size: 36 })}
                          </div>
                          <h3 className="text-4xl font-black uppercase mb-6 tracking-tight">
                              <VisualTextEditable value={item.title} onSave={(v) => handleUpdate(`features[${i}].title`, v)} isEditMode={!!isEditMode} />
                          </h3>
                          <p className="text-lg font-light leading-relaxed mb-8 opacity-60 group-hover:opacity-100">
                              <VisualTextEditable value={item.desc} onSave={(v) => handleUpdate(`features[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                          </p>
                          <div className="w-12 h-2 bg-[var(--color-primary)] group-hover:w-full transition-all duration-700" />
                          {isEditMode && <button onClick={() => removeArrayItem('features', i)} className="absolute bottom-4 right-4 text-red-500 font-black uppercase text-[10px]">Remove</button>}
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('features', { title: "New Core", desc: "Brief description of discipline.", icon: "Star" })} className="p-24 border-l-8 border-t-8 border-black flex flex-col items-center justify-center text-gray-200 hover:text-black transition-all cursor-pointer">
                          <Plus size={64} />
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* Programs - Experimental Grid */}
      {mockData.sectionVisibility.programs && (
      <section id="programs" className="py-40 px-6">
          <div className="container mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 mb-32 items-end">
                  <div className="lg:col-span-3">
                       <span className="text-[11px] font-black uppercase tracking-[0.5em] text-[var(--color-primary)] mb-6 block">KNOWLEDGE TRANSFER</span>
                       <h2 className="text-[8vw] lg:text-[7vw] font-black uppercase tracking-tighter leading-none italic">Studio Academics.</h2>
                  </div>
                  <div className="lg:col-span-1 text-right">
                       <Button className="border-4 border-white text-white hover:bg-white hover:text-black rounded-none px-10 h-16 font-black uppercase tracking-widest transition-all">Download Thesis</Button>
                  </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-24">
                  {mockData.programs.map((prog, i) => (
                      <div key={i} className="group relative">
                          <div className="flex flex-col md:flex-row gap-12 items-center">
                              <div className="w-full md:w-1/2 aspect-[4/5] relative overflow-hidden">
                                  <VisualImageEditable 
                                      src={prog.img} 
                                      alt={prog.title}
                                      className="w-full h-full object-cover grayscale brightness-50 group-hover:scale-110 transition-transform duration-1000" 
                                      onImageChange={(u) => handleUpdate(`programs[${i}].img`, u)}
                                      isEditMode={!!isEditMode}
                                  />
                                  <div className="absolute top-6 left-6 bg-[var(--color-accent)] text-black px-4 py-2 font-black uppercase text-[10px] tracking-widest">
                                      {prog.age}
                                  </div>
                              </div>
                              <div className="w-full md:w-1/2">
                                  <div className="text-[8rem] font-black opacity-10 leading-none mb-[-2rem] group-hover:text-[var(--color-primary)] group-hover:opacity-100 transition-all duration-700">0{i+1}</div>
                                  <h3 className="text-5xl font-black uppercase tracking-tighter mb-8 italic">
                                      <VisualTextEditable value={prog.title} onSave={(v) => handleUpdate(`programs[${i}].title`, v)} isEditMode={!!isEditMode} />
                                  </h3>
                                  <p className="text-xl font-light text-gray-500 leading-relaxed mb-10 border-l-4 border-white/10 pl-8">
                                      <VisualTextEditable value={prog.desc} onSave={(v) => handleUpdate(`programs[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                                  </p>
                                  <ArrowRight className="text-white group-hover:translate-x-8 transition-all duration-700" size={48} />
                              </div>
                          </div>
                          {isEditMode && <button onClick={() => removeArrayItem('programs', i)} className="text-red-500 font-black uppercase text-[10px] absolute top-4 right-4">Delete Studio</button>}
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('programs', { title: "New Research Lab", age: "Post-Residency", desc: "Long description of the program.", img: "" })} className="border-4 border-dashed border-white/5 aspect-video flex flex-col items-center justify-center text-gray-700 hover:text-white hover:border-white transition-all cursor-pointer">
                          <Plus size={64} className="mb-4" />
                          <span className="font-black uppercase tracking-widest text-xs">Initialize Lab</span>
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* Facilities - Technical Studios */}
      {mockData.sectionVisibility.facilities && (
      <section id="facilities" className="py-40 bg-[var(--color-secondary)]/10 px-6">
          <div className="container mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 mb-24 border-b-2 border-white/10 pb-12 items-end">
                  <div className="lg:col-span-1">
                      <span className="text-[11px] font-black uppercase tracking-[0.5em] text-[var(--color-accent)]">ENVIRONMENTS</span>
                  </div>
                  <div className="lg:col-span-2">
                       <h2 className="text-[6vw] font-black uppercase tracking-tighter leading-none italic">The Zero Ground.</h2>
                  </div>
                  <div className="lg:col-span-1 text-right gap-4 flex justify-end">
                       <button className="w-16 h-16 border-2 border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-all"><ChevronLeft size={24}/></button>
                       <button className="w-16 h-16 border-2 border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-all"><ChevronRight size={24}/></button>
                  </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                  {mockData.facilities.map((fac, i) => (
                      <div key={i} className="group relative grid grid-cols-1 md:grid-cols-12 gap-0 border-4 border-white/5 hover:border-[var(--color-primary)] transition-all duration-500">
                          <div className="md:col-span-7 h-[500px] relative overflow-hidden">
                              <VisualImageEditable 
                                  src={fac.img} alt={fac.title}
                                  className="w-full h-full object-cover grayscale brightness-50 group-hover:scale-105 transition-all duration-1000"
                                  onImageChange={(u) => handleUpdate(`facilities[${i}].img`, u)}
                                  isEditMode={!!isEditMode}
                              />
                              <div className="absolute top-0 right-0 p-8 bg-[var(--color-primary)] text-white shadow-2xl">
                                  {React.createElement(getIcon(fac.icon), { size: 32 })}
                              </div>
                          </div>
                          <div className="md:col-span-5 p-12 flex flex-col justify-end bg-black">
                              <div className="text-[10px] font-black mb-6 opacity-30 tracking-[0.4em]">FACILITY // {i.toString().padStart(2, '0')}</div>
                              <h3 className="text-4xl font-black uppercase mb-6 italic leading-none">
                                  <VisualTextEditable value={fac.title} onSave={(v) => handleUpdate(`facilities[${i}].title`, v)} isEditMode={!!isEditMode} />
                              </h3>
                              <p className="text-gray-500 font-light leading-relaxed mb-8">
                                  <VisualTextEditable value={fac.desc} onSave={(v) => handleUpdate(`facilities[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                              </p>
                              <div className="w-full h-[2px] bg-white/10 group-hover:bg-[var(--color-primary)] transition-colors" />
                          </div>
                          {isEditMode && <button onClick={() => removeArrayItem('facilities', i)} className="text-red-500 font-black uppercase text-[10px] absolute bottom-8 left-8">Erase</button>}
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('facilities', { title: "New Development Space", desc: "Detailed description of studio.", img: "", icon: "Zap" })} className="h-[500px] border-4 border-dashed border-white/5 flex flex-col items-center justify-center text-gray-700 hover:text-white hover:border-white transition-all cursor-pointer bg-black/20">
                          <Plus size={48} />
                          <span className="font-black uppercase tracking-widest text-xs mt-4">Initialize Space</span>
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* Gallery - Exposition Style */}
      {mockData.sectionVisibility.gallery && (
      <section id="gallery" className="py-40 px-6">
          <div className="container mx-auto">
              <div className="text-center mb-40">
                  <span className="text-[11px] font-black uppercase tracking-[0.8em] text-[var(--color-primary)] mb-8 block font-black">EXPOSITION 2026</span>
                  <h2 className="text-[12vw] font-black uppercase tracking-tighter leading-none italic mix-blend-screen overflow-hidden">Exhibition.</h2>
                  <div className="flex flex-wrap justify-center gap-10 mt-16 border-y border-white/5 py-10">
                       {["All", "Digital", "Fine Art", "Media", "Sound"].map((tab) => (
                          <button key={tab} onClick={() => setActiveGalleryTab(tab)} className={`text-[11px] font-black uppercase tracking-[0.5em] transition-all relative ${activeGalleryTab === tab ? 'text-[var(--color-primary)]' : 'text-gray-500 hover:text-white'}`}>
                              {tab}
                              {activeGalleryTab === tab && <div className="absolute -bottom-4 left-0 w-full h-[4px] bg-[var(--color-primary)] shadow-[0_0_20px_var(--color-primary)]" />}
                          </button>
                      ))}
                  </div>
              </div>
              
              <div className="columns-1 md:columns-2 lg:columns-3 gap-8 space-y-8">
                  {mockData.gallery.filter(item => activeGalleryTab === "All" || item.category === activeGalleryTab).map((item, i) => (
                      <div key={i} className="break-inside-avoid relative group border-4 border-white/5 bg-black p-4">
                          <VisualImageEditable 
                              src={item.img} alt={item.title}
                              className="w-full grayscale group-hover:grayscale-0 transition-all duration-[2000ms] "
                              onImageChange={(u) => handleUpdate(`gallery[${i}].img`, u)}
                              isEditMode={!!isEditMode}
                          />
                          <div className="p-8 pb-0">
                              <div className="flex justify-between items-end">
                                  <div>
                                      <p className="text-[10px] font-black text-[var(--color-accent)] uppercase tracking-widest mb-2 italic">{item.category}</p>
                                      <h4 className="text-3xl font-black uppercase italic leading-none">{item.title}</h4>
                                  </div>
                                  <span className="text-[10px] font-black font-mono text-white/10 uppercase tracking-tighter">REF_{i.toString().padStart(4, '0')}</span>
                              </div>
                          </div>
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('gallery', { category: "Digital", title: "New Exhibit", img: "" })} className="w-full aspect-square border-4 border-dashed border-white/5 flex flex-col items-center justify-center text-gray-700 hover:text-white transition-all cursor-pointer">
                          <Camera size={64} className="mb-4" />
                          <span className="font-black uppercase tracking-widest text-xs">Register Exhibit</span>
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* Team - Radical Mentors */}
      {mockData.sectionVisibility.team && (
      <section id="team" className="py-40 bg-white text-black px-6">
          <div className="container mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-24 mb-32 items-end border-b-8 border-black pb-12">
                  <div className="lg:col-span-8">
                      <span className="text-[11px] font-black uppercase tracking-[0.5em] text-gray-400 mb-6 block">EXPERTS / DISRUPTORS</span>
                      <h2 className="text-[9vw] font-black uppercase tracking-tighter leading-none italic">The Mentors.</h2>
                  </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                  {mockData.team.map((m, i) => (
                      <div key={i} className="group relative flex flex-col">
                          <div className="relative mb-10 overflow-hidden aspect-[3/4] border-8 border-black p-2">
                               <VisualImageEditable 
                                  src={m.img} 
                                  alt={m.name} 
                                  className="w-full h-full object-cover grayscale brightness-50 group-hover:brightness-100 group-hover:grayscale-0 transition-all duration-[1000ms]" 
                                  onImageChange={(u) => handleUpdate(`team[${i}].img`, u)} 
                                  isEditMode={!!isEditMode}
                              />
                               <div className="absolute top-6 right-6 p-4 bg-black text-white mix-blend-difference">
                                   <Zap size={24} className="group-hover:text-[var(--color-primary)] transition-colors" />
                               </div>
                          </div>
                          <div className="text-center md:text-left">
                              <h3 className="text-4xl font-black uppercase italic tracking-tighter mb-2 group-hover:translate-x-4 transition-transform duration-500">
                                  <VisualTextEditable value={m.name} onSave={(v) => handleUpdate(`team[${i}].name`, v)} isEditMode={!!isEditMode} />
                              </h3>
                              <p className="text-[12px] font-black uppercase tracking-[0.4em] text-[var(--color-primary)]">
                                  <VisualTextEditable value={m.role} onSave={(v) => handleUpdate(`team[${i}].role`, v)} isEditMode={!!isEditMode} />
                              </p>
                          </div>
                           {isEditMode && <button onClick={() => removeArrayItem('team', i)} className="text-red-500 font-black uppercase text-[10px] absolute top-[-20px] left-0">Recall Mentor</button>}
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('team', { name: "New Disruptor", role: "Specialist", img: "" })} className="aspect-[3/4] border-8 border-dashed border-black/10 flex flex-col items-center justify-center text-gray-200 hover:text-black transition-all cursor-pointer">
                          <Plus size={64} />
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* News & Dispatch - High Contrast Feed */}
      {mockData.sectionVisibility.news && (
      <section className="py-40 px-6">
          <div className="container mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-32 items-end border-b-2 border-white/10 pb-12">
                   <div className="lg:col-span-8">
                       <span className="text-[11px] font-black uppercase tracking-[0.5em] text-[var(--color-secondary)] mb-6 block">RADICAL FEED</span>
                       <h2 className="text-[7vw] font-black uppercase tracking-tighter leading-none italic">The Dispatch.</h2>
                   </div>
                   <div className="lg:col-span-4 text-right">
                       <button className="text-[11px] font-black uppercase tracking-[0.4em] border-b-4 border-[var(--color-primary)] pb-4 hover:text-[var(--color-primary)] transition-colors">Enter Archive</button>
                   </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                  {mockData.news.map((item, i) => (
                      <div key={i} className="group flex flex-col bg-white/5 border-l-8 border-[var(--color-primary)] p-12 hover:bg-white hover:text-black transition-all duration-700">
                          <div className="text-[11px] font-black uppercase tracking-widest text-[var(--color-primary)] mb-8 flex justify-between">
                              <span>{item.date}</span>
                              <span className="opacity-30">NEWS // {i+1}</span>
                          </div>
                          <h3 className="text-4xl font-black uppercase tracking-tighter leading-[0.9] mb-10 italic group-hover:translate-x-6 transition-transform duration-500">
                              <VisualTextEditable value={item.title} onSave={(v) => handleUpdate(`news[${i}].title`, v)} isEditMode={!!isEditMode} />
                          </h3>
                          <div className="h-64 overflow-hidden mb-10 relative">
                               <VisualImageEditable 
                                  src={item.img} alt={item.title}
                                  className="w-full h-full object-cover grayscale brightness-50 group-hover:brightness-100 transition-all duration-1000"
                                  onImageChange={(u) => handleUpdate(`news[${i}].img`, u)}
                                  isEditMode={!!isEditMode}
                              />
                               <div className="absolute inset-0 border-4 border-white/0 group-hover:border-black/10 transition-all" />
                          </div>
                          <p className="text-lg font-light leading-relaxed mb-10 opacity-60 group-hover:opacity-100">
                              <VisualTextEditable value={item.desc} onSave={(v) => handleUpdate(`news[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                          </p>
                          <div className="mt-auto pt-8 border-t border-white/10 group-hover:border-black/10 flex items-center justify-between">
                              <span className="text-[10px] font-black uppercase tracking-widest">ACCESS DATA</span>
                              <ArrowUpRight size={24} className="group-hover:rotate-45 transition-transform" />
                          </div>
                      </div>
                  ))}
              </div>
          </div>
      </section>
      )}

      {/* Downloads / Resources - Heavy Industrial Style */}
      {mockData.sectionVisibility.downloads && (
      <section className="py-40 bg-white text-black px-6">
          <div className="container mx-auto px-6 max-w-5xl">
              <div className="text-center mb-24">
                  <span className="text-[11px] font-black uppercase tracking-[1em] text-[var(--color-primary)] mb-8 block">
                      <VisualTextEditable 
                          value={mockData.labels.downloadsSection} 
                          onSave={(v) => handleUpdate('labels.downloadsSection', v)} 
                          isEditMode={!!isEditMode} 
                          as="span" 
                      />
                  </span>
                  <h2 className="text-[6vw] font-black uppercase tracking-tighter leading-none italic ">
                      <VisualTextEditable 
                          value={mockData.labels.downloadsTitle} 
                          onSave={(v) => handleUpdate('labels.downloadsTitle', v)} 
                          isEditMode={!!isEditMode} 
                          as="span" 
                      />
                  </h2>
              </div>
              
              <div className="space-y-4">
                  {mockData.downloads.map((doc, i) => (
                      <div key={i} className="group bg-black text-white p-12 flex flex-col md:flex-row justify-between items-start md:items-center border-l-[16px] border-[var(--color-primary)] hover:translate-x-4 transition-all duration-500 cursor-pointer">
                          <div className="flex items-center gap-12">
                              <div className="p-6 bg-white/10 group-hover:bg-[var(--color-primary)] transition-colors">
                                  <Download size={48} className="text-white" />
                              </div>
                              <div>
                                  <h4 className="text-3xl font-black uppercase tracking-tighter leading-none mb-3">
                                      <VisualTextEditable value={doc.title} onSave={(v) => handleUpdate(`downloads[${i}].title`, v)} isEditMode={!!isEditMode} />
                                  </h4>
                                  <p className="text-[11px] font-black uppercase tracking-[0.3em] opacity-30 italic">
                                      SIZE: <VisualTextEditable value={doc.size} onSave={(v) => handleUpdate(`downloads[${i}].size`, v)} isEditMode={!!isEditMode} as="span" /> // 
                                      EXT: <VisualTextEditable value={doc.type} onSave={(v) => handleUpdate(`downloads[${i}].type`, v)} isEditMode={!!isEditMode} as="span" />
                                  </p>
                              </div>
                          </div>
                          <div className="flex gap-12 items-center mt-12 md:mt-0">
                              <ArrowRight size={48} className="opacity-20 group-hover:opacity-100 group-hover:translate-x-4 transition-all" />
                              {isEditMode && <button onClick={() => removeArrayItem('downloads', i)} className="text-red-500 font-black uppercase text-[10px]">Delete Document</button>}
                          </div>
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('downloads', { title: "New Documentation", size: "1.0 MB", type: "PDF" })} className="p-12 border-8 border-dashed border-black/10 flex flex-col items-center justify-center text-gray-200 hover:text-black transition-all cursor-pointer">
                          <Plus size={48} />
                          <span className="font-black uppercase tracking-widest text-xs mt-4">Append Protocol</span>
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* CTA - The Final Call */}
      {mockData.sectionVisibility.cta && (
      <section id="cta" className="py-64 bg-black relative overflow-hidden text-center px-6">
          <div className="absolute inset-0 bg-[var(--color-primary)] opacity-10 blur-[200px] animate-pulse" />
          <div className="container mx-auto relative z-10">
              <h2 className="text-[12vw] font-black uppercase tracking-tighter leading-none italic mb-16 mix-blend-screen scale-110">
                  Defy <br/> Gravity.
              </h2>
              <p className="text-2xl md:text-5xl font-light text-gray-500 mb-24 italic max-w-4xl mx-auto leading-tight">
                  The future doesn't wait for permission. Join the collective and start defining what's next.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-10">
                   <Button className="bg-[var(--color-primary)] text-white rounded-none h-24 px-16 font-black uppercase tracking-[0.5em] text-xs shadow-[12px_12px_0px_#ffffff20] hover:translate-x-2 hover:translate-y-2 hover:shadow-none transition-all">
                       Initialize Journey <ArrowRight size={24} className="ml-4" />
                   </Button>
                   <Button variant="ghost" className="border-4 border-white text-white hover:bg-white hover:text-black rounded-none h-24 px-16 font-black uppercase tracking-[0.5em] text-xs transition-all">
                       Request Manifest
                   </Button>
              </div>
          </div>
      </section>
      )}

      {/* Footer - Brutalist Minimalism */}
      <footer id="contact" className="bg-white text-black py-40 px-6 border-t-[32px] border-black">
          <div className="container mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-24 mb-40 items-start">
                  <div className="lg:col-span-5">
                      <div className="text-6xl font-black uppercase tracking-tighter mb-12 italic leading-none">{schoolName}.</div>
                      <p className="text-2xl font-light leading-relaxed mb-16 italic opacity-50 border-l-8 border-black pl-8 max-w-md">"We exist in the friction between what is and what could be. Join the disruption."</p>
                      <div className="flex gap-4">
                          {[Facebook, Instagram, Youtube, Linkedin].map((Icon, i) => (
                              <a key={i} href="#" className="w-16 h-16 border-4 border-black flex items-center justify-center hover:bg-black hover:text-white transition-all"><Icon size={28} /></a>
                          ))}
                      </div>
                  </div>
                  
                  <div className="lg:col-span-2">
                      <h4 className="text-[11px] font-black tracking-[0.6em] uppercase text-gray-300 mb-12">DIRECTORY</h4>
                      <ul className="space-y-6 text-[12px] font-black uppercase tracking-widest">
                          {mockData.nav.map((item, i) => (
                              <li key={i}><a href={item.href} className="hover:text-[var(--color-primary)] transition-all flex items-center gap-4 group">[{i+1}] {item.label}</a></li>
                          ))}
                      </ul>
                  </div>

                  <div className="lg:col-span-3">
                      <h4 className="text-[11px] font-black tracking-[0.6em] uppercase text-gray-300 mb-12">ACCESS POINT</h4>
                      <div className="space-y-12">
                          <div className="flex gap-8 group">
                              <MapPin size={32} className="text-[var(--color-primary)] shrink-0" />
                              <div>
                                  <p className="text-[11px] font-black tracking-widest uppercase opacity-20 mb-3 italic">Location_Unit</p>
                                  <p className="text-lg font-black leading-tight uppercase">
                                      <VisualTextEditable value={mockData.contact.address} onSave={(v) => handleUpdate('contact.address', v)} isEditMode={!!isEditMode} multiline />
                                  </p>
                              </div>
                          </div>
                          <div className="flex gap-8 group">
                              <Phone size={32} className="text-[var(--color-secondary)] shrink-0" />
                              <div>
                                  <p className="text-[11px] font-black tracking-widest uppercase opacity-20 mb-3 italic">Direct_Link</p>
                                  <p className="text-lg font-black uppercase">
                                      <VisualTextEditable value={mockData.contact.phone} onSave={(v) => handleUpdate('contact.phone', v)} isEditMode={!!isEditMode} />
                                  </p>
                              </div>
                          </div>
                      </div>
                  </div>

                  <div className="lg:col-span-2">
                      <h4 className="text-[11px] font-black tracking-[0.6em] uppercase text-gray-300 mb-12">JOIN_COMM</h4>
                      <div className="space-y-8">
                           <p className="text-xs text-gray-400 italic font-medium leading-relaxed">Submit your institutional email to join the radical feed.</p>
                           <div className="flex gap-2 border-b-4 border-black pb-4 hover:border-[var(--color-primary)] transition-all group">
                                <input type="email" placeholder="USER@ORG.COM" className="bg-transparent border-none focus:ring-0 text-[11px] font-black tracking-widest w-full placeholder:text-gray-200" />
                                <button className="text-gray-200 hover:text-black transition-colors"><Send size={24} /></button>
                           </div>
                      </div>
                  </div>
              </div>
              
              <div className="pt-24 border-t-8 border-black flex flex-col md:flex-row justify-between items-center gap-12 text-[10px] font-black tracking-[0.5em] uppercase text-gray-300">
                  <p>&copy; {new Date().getFullYear()} {schoolName} // DEFINE WHAT'S NEXT // ALL DATA SECURE</p>
                  <div className="flex gap-16">
                      <a href="#" className="hover:text-black transition-colors">TERMINOLOGY</a>
                      <a href="#" className="hover:text-black transition-colors">STRUCTURE</a>
                  </div>
              </div>
          </div>
      </footer>

      {/* Mobile Menu Overlay - Industrial Blackout */}
      {menuOpen && (
          <div className="fixed inset-0 z-[100] bg-black p-12 flex flex-col justify-between animate-in fade-in slide-in-from-bottom duration-500">
              <div className="flex justify-between items-center">
                  <div className="text-3xl font-black uppercase tracking-tighter text-white italic">{schoolName}.</div>
                  <button onClick={() => setMenuOpen(false)} className="bg-white text-black p-4"><X size={32} /></button>
              </div>
              <nav className="flex flex-col gap-10">
                  {mockData.nav.filter(n => n.visible).map((n, i) => (
                      <a key={i} href={n.href} onClick={() => setMenuOpen(false)} className="text-[12vw] font-black uppercase tracking-tighter leading-none italic text-white hover:text-[var(--color-primary)] transition-all">
                          {n.label}
                      </a>
                  ))}
              </nav>
              <div className="pt-20 border-t-4 border-white/5">
                  <Button className="w-full bg-[var(--color-primary)] text-white rounded-none h-24 font-black uppercase tracking-[0.5em] text-xs">Initialize Membership</Button>
              </div>
          </div>
      )}
    </div>
  );
};

export default CreativeArts;
